import { useContext, useCallback, useMemo } from 'react'
import { AuthContext } from '../context/AuthContext'
import { customFetch as utilsCustomFetch, invalidateCache } from '../utils/fetch'

export const useCustomFetch = () => {
  const { googleSignOut, microsoftSignOut } = useContext(AuthContext)

  // Memoize the customFetch function using useCallback
  const customFetch = useCallback(
    async (urlPath, method, body, extraOptions, parseJson = true) => {
      // Use the enhanced utilsCustomFetch
      // Auth headers and token refresh are now handled within utilsCustomFetch
      const response = await utilsCustomFetch(urlPath, method, body, extraOptions, parseJson)

      // Handle 401 auth errors: trigger sign-out
      if (response && response.error && response.error.status === 401) {
        console.error('Unauthorized access detected. Signing out.')
        const signinMethod = localStorage.getItem('signin_method')
        if (signinMethod === 'google') {
          googleSignOut()
        } else if (signinMethod === 'microsoft') {
          microsoftSignOut()
        } else {
          // Fallback: clear local storage and redirect to home
          localStorage.clear()
          window.location.href = '/'
        }
        // Optionally return or throw an error to indicate failure
        // Depending on how callers should react to sign-out
        return { error: { message: 'User signed out due to authorization failure.' } }
      }

      // Return the response (data or error object)
      return response
    },
    [googleSignOut, microsoftSignOut]
  )

  // Memoize the return value to ensure the object reference is stable
  const hookReturnValue = useMemo(() => {
    // Assign invalidateCache (which is stable) to the memoized customFetch function
    return Object.assign(customFetch, {
      invalidateCache,
    })
  }, [customFetch])

  return hookReturnValue
}

export const authCustomFetch = async (urlPath, method, body) => {
  try {
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    }
    if (import.meta.env.VITE_NODE_ENV !== 'willUseHttpOnly') {
      options.headers.authorization = localStorage.getItem('access_token')
      options.headers.signinmethod = localStorage.getItem('signin_method')
    }
    if (body) options.body = JSON.stringify(body)

    const response = await fetch(`${import.meta.env.VITE_SERVER_URL}/api${urlPath}`, options)

    if (!response.ok) throw await response.json()

    // Handle token refresh in the original way for this specific function
    if (import.meta.env.VITE_NODE_ENV !== 'willUseHttpOnly') {
      response.headers.forEach((value, key) => {
        if (key === 'set-access-token') {
          localStorage.setItem('access_token', value)
        }
      })
    }

    return await response.json()
  } catch (error) {
    return { error }
  }
}
